import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, CheckCircle, XCircle, Clock } from "lucide-react";
import { format } from "date-fns";

export default function AthleteCompensation() {
  const queryClient = useQueryClient();
  const [isAllocateDialogOpen, setIsAllocateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    athlete_id: "",
    pool_id: "",
    amount: "",
    distribution_date: "",
    notes: "",
  });

  const { data: compensations, isLoading } = useQuery({
    queryKey: ["athlete_compensation"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athlete_compensation" as any)
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: athletes } = useQuery({
    queryKey: ["athletes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athletes" as any)
        .select("id, name, sport");
      
      if (error) throw error;
      return data;
    },
  });

  const { data: pools } = useQuery({
    queryKey: ["revenue_sharing_pools"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("revenue_sharing_pools" as any)
        .select("*")
        .eq("status", "active");
      
      if (error) throw error;
      return data;
    },
  });

  const allocateCompensationMutation = useMutation({
    mutationFn: async (compensation: typeof formData) => {
      const { error } = await supabase
        .from("athlete_compensation" as any)
        .insert([{
          ...compensation,
          amount: parseFloat(compensation.amount),
          distribution_date: compensation.distribution_date || null,
        }]);
      if (error) throw error;

      // Update pool allocated amount
      const pool = (pools as any[])?.find((p: any) => p.id === compensation.pool_id);
      if (pool) {
        const { error: updateError } = await supabase
          .from("revenue_sharing_pools" as any)
          .update({
            allocated_amount: (pool.allocated_amount as number) + parseFloat(compensation.amount),
          })
          .eq("id", compensation.pool_id);
        
        if (updateError) throw updateError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["athlete_compensation"] });
      queryClient.invalidateQueries({ queryKey: ["revenue_sharing_pools"] });
      toast.success("Compensation allocated successfully");
      setIsAllocateDialogOpen(false);
      setFormData({
        athlete_id: "",
        pool_id: "",
        amount: "",
        distribution_date: "",
        notes: "",
      });
    },
    onError: (error) => {
      toast.error("Failed to allocate compensation: " + error.message);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from("athlete_compensation" as any)
        .update({
          status,
          approved_by: status === "approved" ? user?.id : null,
          approved_at: status === "approved" ? new Date().toISOString() : null,
        })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["athlete_compensation"] });
      toast.success("Status updated successfully");
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      pending: "secondary",
      approved: "default",
      distributed: "default",
      cancelled: "destructive",
    };
    return variants[status] || "secondary";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
      case "distributed":
        return <CheckCircle className="h-4 w-4" />;
      case "cancelled":
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getAthleteById = (id: string) => {
    return athletes?.find((a: any) => a.id === id);
  };

  const getPoolById = (id: string) => {
    return pools?.find((p: any) => p.id === id);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Athlete Compensation</h1>
            <p className="text-muted-foreground">
              Allocate revenue sharing to athletes from pools
            </p>
          </div>
          <Dialog open={isAllocateDialogOpen} onOpenChange={setIsAllocateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Allocate Compensation
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Allocate Athlete Compensation</DialogTitle>
                <DialogDescription>
                  Allocate funds from a revenue sharing pool to an athlete
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="athlete_id">Athlete *</Label>
                  <Select
                    value={formData.athlete_id}
                    onValueChange={(value) => setFormData({ ...formData, athlete_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select athlete" />
                    </SelectTrigger>
                    <SelectContent>
                      {athletes?.map((athlete: any) => (
                        <SelectItem key={athlete.id} value={athlete.id}>
                          {athlete.name} - {athlete.sport}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pool_id">Revenue Pool *</Label>
                  <Select
                    value={formData.pool_id}
                    onValueChange={(value) => setFormData({ ...formData, pool_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select pool" />
                    </SelectTrigger>
                    <SelectContent>
                      {pools?.map((pool: any) => (
                        <SelectItem key={pool.id} value={pool.id}>
                          {pool.sport} ({pool.gender}) - {pool.academic_year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount *</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="distribution_date">Distribution Date</Label>
                    <Input
                      id="distribution_date"
                      type="date"
                      value={formData.distribution_date}
                      onChange={(e) => setFormData({ ...formData, distribution_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAllocateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => allocateCompensationMutation.mutate(formData)}
                  disabled={!formData.athlete_id || !formData.pool_id || !formData.amount || allocateCompensationMutation.isPending}
                >
                  Allocate
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Compensation Records</CardTitle>
            <CardDescription>All athlete compensation allocations</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div>Loading...</div>
            ) : compensations && compensations.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Athlete</TableHead>
                    <TableHead>Pool</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Distribution Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(compensations as any[]).map((comp: any) => {
                    const athlete = getAthleteById(comp.athlete_id);
                    const pool = getPoolById(comp.pool_id) as any;
                    
                    return (
                      <TableRow key={comp.id}>
                        <TableCell>{(athlete as any)?.name || "Unknown"}</TableCell>
                        <TableCell>
                          {pool ? `${pool.sport} (${pool.gender})` : "Unknown"}
                        </TableCell>
                        <TableCell>{formatCurrency(comp.amount)}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadge(comp.status)} className="flex items-center gap-1 w-fit">
                            {getStatusIcon(comp.status)}
                            {comp.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {comp.distribution_date 
                            ? format(new Date(comp.distribution_date), "MMM d, yyyy")
                            : "Not set"}
                        </TableCell>
                        <TableCell>
                          {comp.status === "pending" && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => updateStatusMutation.mutate({ id: comp.id, status: "approved" })}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => updateStatusMutation.mutate({ id: comp.id, status: "cancelled" })}
                              >
                                Cancel
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No compensation records found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
